import React, { useEffect, useState } from "react";
import { Button, Spacer } from "@heroui/react";
import { LazyMotion, domAnimation, m, useReducedMotion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { calcReadTime, getPostList, type Post } from "../../lib/queries";
import { blogUrl } from "../../lib/siteUrl";

const PREVIEW_COUNT = 3;
const easeOut = [0.22, 1, 0.36, 1] as const;

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString("zh-CN", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

function buildMetaLine(post: Post, readMinutes: number) {
  return `约 ${readMinutes} 分钟 · ${formatDate(post.publishedAt)}`;
}

export default function LatestNewsSection() {
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const reduceMotion = useReducedMotion();

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getPostList(1, PREVIEW_COUNT)
      .then(({ posts: list }) => {
        if (!cancelled) {
          setPosts(list);
          setLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setPosts([]);
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const container = {
    hidden: { opacity: reduceMotion ? 1 : 0 },
    show: {
      opacity: 1,
      transition: reduceMotion
        ? { duration: 0 }
        : {
            staggerChildren: 0.06,
            delayChildren: 0.04,
          },
    },
  };

  const item = {
    hidden: { opacity: reduceMotion ? 1 : 0, y: reduceMotion ? 0 : 18 },
    show: {
      opacity: 1,
      y: 0,
      transition: { duration: reduceMotion ? 0 : 0.5, ease: easeOut },
    },
  };

  return (
    <section
      id="news"
      className="mx-auto w-full max-w-6xl py-20 sm:px-6 sm:py-32 lg:px-8 lg:py-40"
    >
      <LazyMotion features={domAnimation}>
        <div className="relative mx-auto flex w-full flex-col items-center">
          <div className="flex w-full max-w-xl flex-col text-center">
            <p className="text-[13px] font-medium tracking-wide text-[#00b7ff] sm:text-sm">
              最新动态
            </p>
            <h2 className="text-foreground mt-5 text-2xl leading-7 font-medium sm:mt-6 sm:text-4xl">产品更新与行业洞察</h2>
            <Spacer y={4} />
            <h2 className="text-large text-default-500">持续分享 AI 销冠的产品进展、行业方法和真实业务实践</h2>
          </div>
          <Spacer y={8} />

          {loading ? (
            <div className="flex w-full max-w-4xl flex-col gap-[18px]">
              {Array.from({ length: 3 }).map((_, i) => (
                <div
                  key={i}
                  className="flex animate-pulse flex-col gap-4 rounded-2xl border border-default-100 bg-default-100/50 p-4 sm:flex-row sm:items-stretch sm:gap-4"
                >
                  <div className="aspect-[260/156] w-full shrink-0 rounded-xl bg-default-100 sm:w-[260px]" />
                  <div className="flex min-w-0 flex-1 flex-col justify-between gap-3 py-1">
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <div className="h-3 w-1/3 rounded bg-default-100" />
                        <div className="h-6 w-4/5 rounded bg-default-100" />
                      </div>
                      <div className="h-10 w-full rounded bg-default-100" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : posts.length === 0 ? (
            <p className="relative w-full max-w-4xl py-16 text-center text-sm text-default-500 dark:text-[#9FB0D4]">
              暂无文章
            </p>
          ) : (
            <m.ul
              className="relative flex w-full max-w-4xl flex-col gap-[18px]"
              variants={container}
              initial="hidden"
              animate="show"
            >
              {posts.map((post) => {
                const slug = post.slug?.current?.trim();
                if (!slug) return null;
                const readTimeMinutes = calcReadTime(post.body);
                return (
                  <m.li key={post._id} variants={item} className="group relative">
                    <a
                      href={blogUrl(slug)}
                      className="relative flex flex-col gap-4 rounded-2xl border border-default-100 bg-default-100/50 p-4 shadow-sm backdrop-blur-md transition-all duration-300 hover:border-[#24324D] hover:bg-default-100/90 hover:shadow-[0_12px_40px_-18px_rgba(0,0,0,0.12)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:ring-offset-2 focus-visible:ring-offset-background dark:bg-[#0f172a]/30 dark:hover:bg-[#0f172a]/55 sm:flex-row sm:items-stretch sm:gap-4"
                    >
                      <div className="relative w-full shrink-0 overflow-hidden rounded-xl transition-transform duration-300 sm:w-[260px]">
                        <div className="aspect-[260/156] w-full bg-default-100">
                          {post.coverImage ? (
                            <img
                              src={post.coverImage}
                              alt={post.title}
                              className="h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.03]"
                            />
                          ) : (
                            <div className="flex h-full w-full items-end justify-start bg-gradient-to-br from-[#2443A8] via-[#1c2d5c] to-[#1C2A53] p-4 dark:from-[#2443A8] dark:to-[#1C2A53]">
                              <span className="text-[11px] font-medium tracking-[0.35em] text-white/80">
                                3CHAT
                              </span>
                            </div>
                          )}
                        </div>
                        <div
                          className="pointer-events-none absolute inset-0 rounded-xl bg-gradient-to-t from-background/25 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100 dark:from-black/30"
                          aria-hidden
                        />
                      </div>

                      <div className="flex min-w-0 flex-1 flex-col justify-between gap-3">
                        <div className="space-y-4">
                          <div className="space-y-1">
                            <p className="text-[13px] leading-relaxed text-default-500 dark:text-default-500">
                              {buildMetaLine(post, readTimeMinutes)}
                            </p>
                            <h3 className="line-clamp-1 text-2xl font-bold leading-[1.2] tracking-tight text-foreground transition-colors duration-300 dark:text-[#F2F6FF]">
                              {post.title}
                            </h3>
                          </div>
                          {post.excerpt ? (
                            <p className="line-clamp-2 text-base leading-[1.45] text-default-500 transition-colors duration-300 dark:text-default-500 group-hover:text-default-600 dark:group-hover:text-[#c5d4eb]">
                              {post.excerpt}
                            </p>
                          ) : null}
                        </div>

                        <div className="flex justify-end pt-1">
                          <span className="-translate-x-2 inline-flex items-center gap-1 text-sm font-semibold text-default-500 opacity-0 transition-[transform,opacity] duration-300 ease-out motion-reduce:translate-x-0 group-hover:translate-x-0 group-hover:opacity-100 group-focus-within:translate-x-0 group-focus-within:opacity-100">
                            阅读全文
                            <ArrowRight
                              className="h-4 w-4 transition-transform duration-300 ease-out group-hover:translate-x-0.5"
                              strokeWidth={2}
                              aria-hidden
                            />
                          </span>
                        </div>
                      </div>
                    </a>
                  </m.li>
                );
              })}
            </m.ul>
          )}

          <div className="mt-12 flex w-full justify-center">
            <Button
              as="a"
              className="border-default-100 text-small h-10 w-fit border-1 px-[16px] py-[10px] leading-5 font-medium transition-[background-color,border-color,color,box-shadow] duration-300 ease-out hover:border-default-400 hover:bg-default-100/60 hover:text-default-foreground hover:shadow-md hover:shadow-black/25"
              href={blogUrl()}
              radius="full"
              variant="bordered"
            >
              查看全部
            </Button>
          </div>
        </div>
      </LazyMotion>
    </section>
  );
}
